import type { ReactNode } from 'react';
import { WEB_ICO } from '../../shell/icons';
import { screenShell } from '../lesson/common';

/** Layout dùng chung cho 3 màn pháp lý. */
export function LegalScreen({ onBack, title, children }: { onBack: () => void; title: string; children?: ReactNode }) {
  return (
    <div style={screenShell}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '14px 20px 10px', borderBottom: '1px solid var(--border-default)' }}>
        <button
          onClick={onBack}
          aria-label="Quay lại"
          style={{ width: 44, height: 44, marginLeft: -10, border: 'none', background: 'none', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
        >
          {WEB_ICO.back('var(--ink)', 24)}
        </button>
        <h1 style={{ fontSize: 19, fontWeight: 700, color: 'var(--ink)', margin: 0 }}>{title}</h1>
      </div>
      <div style={{ flex: 1, overflowY: 'auto', padding: '18px 20px 28px', fontSize: 14, lineHeight: 1.75, color: 'var(--text-secondary)', display: 'flex', flexDirection: 'column', gap: 14 }}>
        {children}
      </div>
    </div>
  );
}

function Heading({ children }: { children: string }) {
  return <div style={{ fontSize: 14, fontWeight: 700, color: 'var(--ink)', marginBottom: -8 }}>{children}</div>;
}

export function AboutLicenseScreen({ onBack }: { onBack: () => void }) {
  return (
    <LegalScreen onBack={onBack} title="Giới thiệu & Giấy phép">
      <p style={{ margin: 0 }}>TORII — nền tảng học tiếng Nhật dành cho người Việt.</p>
      <Heading>Nguồn dữ liệu từ vựng &amp; kanji</Heading>
      <p style={{ margin: 0 }}>
        Một phần dữ liệu từ vựng, kanji và cách đọc sử dụng trong app dựa trên JMdict và KANJIDIC2, do Electronic Dictionary Research and Development
        Group (EDRDG) phát hành theo giấy phép Creative Commons Attribution-ShareAlike 4.0.
      </p>
      <Heading>Phiên bản</Heading>
      <p style={{ margin: 0 }}>TORII v0.1 (bản dựng nội bộ)</p>
    </LegalScreen>
  );
}

/// Email liên hệ hiển thị trên các trang pháp lý.
const CONTACT_EMAIL = 'torii.ops@toriilearning.com';

/**
 * Nội dung Chính sách bảo mật — dùng chung cho màn trong app (Cài đặt) và trang
 * công khai /privacy (link khai báo với Google OAuth, phải xem được khi chưa đăng nhập).
 * Đổi nội dung thì đổi cả apps/mobile/src/screens/hoso/PrivacyPolicyScreen.tsx.
 */
export function PrivacyPolicyContent() {
  return (
    <>
      <p style={{ margin: 0 }}>Chúng tôi thu thập thông tin tối thiểu cần thiết để vận hành TORII: email đăng nhập, tiến độ học và dữ liệu ôn tập SRS.</p>
      <Heading>Đăng nhập bằng Google</Heading>
      <p style={{ margin: 0 }}>
        Khi bạn chọn &quot;Tiếp tục với Google&quot;, TORII chỉ nhận từ Google: địa chỉ email, tên hiển thị, ảnh đại diện và mã định danh tài khoản Google.
        Các thông tin này chỉ dùng để tạo và đăng nhập tài khoản TORII của bạn. TORII không truy cập bất kỳ dữ liệu nào khác trong tài khoản Google
        (Gmail, Drive, danh bạ...) và không chia sẻ dữ liệu nhận từ Google cho bên thứ ba.
      </p>
      <Heading>Email</Heading>
      <p style={{ margin: 0 }}>Email của bạn được dùng để gửi mã xác minh khi đăng ký và khi đặt lại mật khẩu. TORII không gửi email quảng cáo.</p>
      <Heading>Chia sẻ dữ liệu</Heading>
      <p style={{ margin: 0 }}>Dữ liệu học tập không được chia sẻ cho bên thứ ba ngoài mục đích vận hành dịch vụ.</p>
      <Heading>Xoá tài khoản</Heading>
      <p style={{ margin: 0 }}>Bạn có thể yêu cầu xoá tài khoản bất kỳ lúc nào trong mục Cài đặt. Tài khoản bị khoá ngay và bị xoá vĩnh viễn sau 30 ngày.</p>
      <p style={{ margin: 0 }}>
        Sau 30 ngày, thông tin định danh (email, tên hiển thị, mật khẩu, ảnh đại diện, liên kết tài khoản Google) bị xoá vĩnh viễn. Dữ liệu học tập
        được giữ lại ở dạng ẩn danh để phục vụ thống kê tổng hợp.
      </p>
      <Heading>Liên hệ</Heading>
      <p style={{ margin: 0 }}>Mọi câu hỏi về dữ liệu cá nhân, vui lòng gửi tới {CONTACT_EMAIL}.</p>
    </>
  );
}

/** Nội dung Điều khoản sử dụng — dùng chung cho màn trong app và trang công khai /terms. */
export function TermsContent() {
  return (
    <>
      <p style={{ margin: 0 }}>Bằng việc sử dụng TORII, bạn đồng ý dùng nội dung học tập cho mục đích cá nhân, không sao chép hay phân phối lại.</p>
      <p style={{ margin: 0 }}>TORII có thể cập nhật nội dung bài học, tính năng SRS và AI theo thời gian để cải thiện trải nghiệm học.</p>
      <p style={{ margin: 0 }}>Việc lạm dụng tính năng AI (chat, chấm phát âm) vượt quá giới hạn công bằng có thể bị tạm khoá.</p>
      <p style={{ margin: 0 }}>Liên hệ: {CONTACT_EMAIL}.</p>
    </>
  );
}

export function PrivacyPolicyScreen({ onBack }: { onBack: () => void }) {
  return (
    <LegalScreen onBack={onBack} title="Privacy Policy">
      <PrivacyPolicyContent />
    </LegalScreen>
  );
}

export function TermsScreen({ onBack }: { onBack: () => void }) {
  return (
    <LegalScreen onBack={onBack} title="Điều khoản sử dụng">
      <TermsContent />
    </LegalScreen>
  );
}
